/* eslint-disable react/display-name */
import { HandWave, Memo } from 'kits/IconKit2';
import IvicosStrings from 'kits/language/stringKit';
import React, { useState } from 'react';
import { useLocalProfile, useOtherUsersStatus } from 'shared-state/identity/hooks';
import { useCurrentAreaId, useCurrentRoomId } from 'shared-state/location/hook';
import { useDirectMessageComposerUserIds } from 'shared-state/messaging/hooks';
import { makeMessageId, useReceivedMessageInbox } from 'shared-state/notifications/hooks';
import { useDirectControlNameWidthInMemo, useIsMemoOpenOnCrossAreaMessagePanel, useShowCrossAreaMessagePanel } from 'shared-state/display-options/hooks';
import { useEmittingEvents } from 'services/socket-connection/socket-adapters/event-actions/emittingEvents';
import { useRoomListIncludingPersonalRooms } from 'shared-state/directory/hooks';
import { IUserAction } from 'features/Rooms/RoomList/helpers/userHooks/userTypes';
import { ICrossUserProfile } from '../../dataTypes';
import { IC } from 'components/SVGIcon';

export const useDirectMessagingActions = (user: ICrossUserProfile): IUserAction[] => {
    const [currentRoomId] = useCurrentRoomId();
    const roomListIncludingPersonalRooms = useRoomListIncludingPersonalRooms();
    const currentRoom = roomListIncludingPersonalRooms.find((room) => room.id === currentRoomId);
    const localProfile: any = useLocalProfile();
    const [currentAreaId] = useCurrentAreaId();
    const [, setDirectMessageComposerUserIds] = useDirectMessageComposerUserIds();
    const [, addMessage] = useReceivedMessageInbox();
    const { crossAreaInviteUserToRoom } = useEmittingEvents();
    const [, setDirectControlNameWidthInMemo] = useDirectControlNameWidthInMemo();
    const [showCrossAreaMessagePanel] = useShowCrossAreaMessagePanel();
    const [isMemoOpenOnCrossAreaMessagePanel, setIsMemoOpenOnCrossAreaMessagePanel] = useIsMemoOpenOnCrossAreaMessagePanel();
    const [isInvited, setIsInvited] = useState(false);
    const chosenLanguage = localStorage.getItem('chosenLanguage');
    const otherUsersStatus = useOtherUsersStatus(user.uid);

    const sendMemoAction: IUserAction = {
        key: 'send-memo',
        title: IvicosStrings.sendUserMemoActionTitle,
        action: () => {
            showCrossAreaMessagePanel && !isMemoOpenOnCrossAreaMessagePanel && setIsMemoOpenOnCrossAreaMessagePanel(true);
            setDirectMessageComposerUserIds([user.uid]);
            setDirectControlNameWidthInMemo(true);
        },
        onRenderIcon: () => (
            <IC variant={user.isUserAloneInRoom ? 'dark' : 'red'} size={20}>
                <Memo />
            </IC>
        )
    };

    const inviteToRoomAction: IUserAction = {
        key: 'invite-user',
        title: isInvited ? (chosenLanguage === 'en' ? 'Invited' : 'Eingeladen') : IvicosStrings.inviteUserToRoomActionTitle,
        action: () => {
            currentRoom && crossAreaInviteUserToRoom(user.uid, currentRoom);
            currentRoom &&
                addMessage({
                    id: makeMessageId(),
                    type: 'confirmation',
                    body: IvicosStrings.invitationSentNotificationActionTitle,
                    timeout: 2000,
                    sender: '',
                    meta: {},
                    timestamp: new Date()
                });

            showCrossAreaMessagePanel && setIsInvited(!isInvited);
        },

        onRenderIcon: () => <HandWave fontSize={20} />
    };

    let actions: any = [];

    // User is in the lobby (outside an area)
    if (!currentAreaId) {
        if (otherUsersStatus && otherUsersStatus.emergencyContact) {
            const { status, endTime, returnDate, emergencyContact } = otherUsersStatus;
            const busyEmergencyContact = emergencyContact as { inviteToRoom?: boolean; knockDoor?: boolean; textMessage?: boolean };
            const oooEmergencyContact = emergencyContact as { email?: boolean; chat?: boolean };

            if (status === 'busy' && endTime && endTime > Date.now()) {
                const textMessage = !!busyEmergencyContact.textMessage;
                if (textMessage) {
                    actions = [sendMemoAction];
                } else {
                    actions = [];
                }
            }

            if (status === 'out-of-office' && returnDate) {
                const email = !!oooEmergencyContact.email;
                const chat = !!oooEmergencyContact.chat;
                if (email && chat) {
                    actions = [sendMemoAction];
                }
                if (email) {
                    actions = [];
                }
            }
        } else {
            actions = [sendMemoAction];
        }
    }
    // User is in an area
    else {
        if (!user.isUserAloneInRoom) {
            if (otherUsersStatus && otherUsersStatus.emergencyContact) {
                const { status, endTime, returnDate, emergencyContact } = otherUsersStatus;
                const busyEmergencyContact = emergencyContact as { inviteToRoom?: boolean; knockDoor?: boolean; textMessage?: boolean };
                const oooEmergencyContact = emergencyContact as { email?: boolean; chat?: boolean };

                if (status === 'busy' && endTime && endTime > Date.now()) {
                    const inviteToRoomEnabled = busyEmergencyContact.inviteToRoom ?? busyEmergencyContact.knockDoor;
                    const textMessage = !!busyEmergencyContact.textMessage;
                    if (inviteToRoomEnabled && textMessage) {
                        actions = [sendMemoAction, inviteToRoomAction];
                    } else if (!inviteToRoomEnabled && !textMessage) {
                        actions = [];
                    } else if (inviteToRoomEnabled) {
                        actions = [inviteToRoomAction];
                    } else {
                        actions = [sendMemoAction];
                    }
                }

                if (status === 'out-of-office' && returnDate) {
                    const email = !!oooEmergencyContact.email;
                    const chat = !!oooEmergencyContact.chat;
                    if (email && chat) {
                        actions = [sendMemoAction];
                    }
                    if (email) {
                        actions = [];
                    }
                }
            } else {
                actions = [];
            }
        } else {
            if (otherUsersStatus && otherUsersStatus.emergencyContact) {
                const { status, endTime, returnDate, emergencyContact } = otherUsersStatus;
                const busyEmergencyContact = emergencyContact as { inviteToRoom?: boolean; knockDoor?: boolean; textMessage?: boolean };
                const oooEmergencyContact = emergencyContact as { email?: boolean; chat?: boolean };

                if (status === 'busy' && endTime && endTime > Date.now()) {
                    const inviteToRoomEnabled = busyEmergencyContact.inviteToRoom ?? busyEmergencyContact.knockDoor;
                    const textMessage = !!busyEmergencyContact.textMessage;
                    if (inviteToRoomEnabled && textMessage) {
                        actions = [sendMemoAction, inviteToRoomAction];
                    } else if (!inviteToRoomEnabled && !textMessage) {
                        actions = [];
                    } else if (inviteToRoomEnabled) {
                        actions = [inviteToRoomAction];
                    } else {
                        actions = [sendMemoAction];
                    }
                }

                if (status === 'out-of-office' && returnDate) {
                    const email = !!oooEmergencyContact.email;
                    const chat = !!oooEmergencyContact.chat;
                    if (email && chat) {
                        actions = [sendMemoAction];
                    }
                    if (email) {
                        actions = [];
                    }
                }
            } else {
                actions = [sendMemoAction, inviteToRoomAction];
            }
        }
    }

    return localProfile?.uid == user.uid ? [] : actions;
};
